/**
 * Feature E06-F04 — Library & History pure model/logic (web side).
 *
 * Framework-free, side-effect-free helpers ported 1:1 from the prototype
 * `../EER app/prototype/assets/research.proto.js` (renderLibrary / renderHistory /
 * openRequest / cloneRequest). Keeping the rules here makes paging-window,
 * "Showing X–Y of N", entity badges, status pills and open-by-status routing
 * unit-testable without a DOM — exactly like the project's other feature models.
 *
 * The list QUERY (search/filter/pagination/sort) is done server-side by
 * `services/researchLibrary.ts`; the helpers here cover the presentation rules
 * the React component reads (badge text, pager window, status colours, routing).
 *
 * Parity anchors (research.proto.js):
 *   - PER = 20 (libPage pagination);
 *   - "Showing <start+1>–<start+pageList.length> of <total>" pager caption;
 *   - entity badge: pure-play → none, division-of → "division of <parent>",
 *     portfolio-item → "portfolio · <parent>";
 *   - statusPill colour/label maps;
 *   - opensTo: draft→Request, running→Run, failed→Run, in_review/published→Review.
 */
import {
  RESEARCH_LIST_PAGE_SIZE,
  COMPANY_ENTITY_BADGE_PREFIX,
  RESEARCH_SCREEN_LABELS,
  opensToScreen,
  type LibraryCompanyRow,
  type HistoryRequestRow,
  type ResearchRequestStatus,
  type ResearchScreen,
} from '@eer/shared';

/** Prototype `PER` — 20 rows per Library page. */
export const LIBRARY_PAGE_SIZE = RESEARCH_LIST_PAGE_SIZE;

// ---------------------------------------------------------------------------
// entity badge (T02 acceptance #4)
// ---------------------------------------------------------------------------

/** Badge text for a Library row, or null for a pure-play company (no badge). */
export function entityBadgeText(
  row: Pick<LibraryCompanyRow, 'entity_type' | 'parent'>,
): string | null {
  if (row.entity_type === 'pure-play' || !row.parent) return null;
  const prefix = COMPANY_ENTITY_BADGE_PREFIX[row.entity_type];
  return `${prefix} ${row.parent}`.trim();
}

// ---------------------------------------------------------------------------
// pager (T02 acceptance #3: "Showing X–Y of N", prev/numbers/next)
// ---------------------------------------------------------------------------

/** Total page count for `total` rows (>=1 so an empty list still shows page 1). */
export function totalPages(total: number, pageSize: number = LIBRARY_PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/** Clamp a requested page into `[1, totalPages]` (prototype `Math.min/Math.max`). */
export function clampPage(
  page: number,
  total: number,
  pageSize: number = LIBRARY_PAGE_SIZE,
): number {
  return Math.min(Math.max(1, page || 1), totalPages(total, pageSize));
}

/**
 * The "Showing X–Y of N" caption inputs for the current page. `from`/`to` are
 * 1-based and inclusive; an empty list yields `from: 0` (prototype: `start+1` is
 * shown as 0 when the list is empty). Exported for the pager footer + tests.
 */
export interface PagerWindow {
  readonly from: number;
  readonly to: number;
  readonly total: number;
  readonly page: number;
  readonly pages: number;
}

export function pagerWindow(
  total: number,
  page: number,
  rowsOnPage: number,
  pageSize: number = LIBRARY_PAGE_SIZE,
): PagerWindow {
  const pages = totalPages(total, pageSize);
  const safePage = clampPage(page, total, pageSize);
  const start = (safePage - 1) * pageSize;
  return {
    from: total > 0 ? start + 1 : 0,
    to: start + rowsOnPage,
    total,
    page: safePage,
    pages,
  };
}

/** Page-number list `[1..pages]` for the pager (prototype `Array.from`). */
export function pageNumbers(total: number, pageSize: number = LIBRARY_PAGE_SIZE): number[] {
  return Array.from({ length: totalPages(total, pageSize) }, (_, i) => i + 1);
}

/** A compact-pager slot: a page number or an ellipsis gap. */
export type PagerItem = number | 'gap-left' | 'gap-right';

/**
 * E06-F04-T09 — compact page list: first, last and `current ± siblings`, with an
 * ellipsis for every skipped run (a run of exactly one page is shown as that
 * page instead of `…`). Bounded at `2 * siblings + 5` slots, so 246 pages no
 * longer render 246 buttons that overflow the screen edge.
 */
export function compactPageItems(pages: number, current: number, siblings = 1): PagerItem[] {
  const last = Math.max(1, pages);
  const cur = Math.min(Math.max(1, current || 1), last);
  // small lists: everything fits in the bounded width anyway
  if (last <= 2 * siblings + 5) {
    return Array.from({ length: last }, (_, i) => i + 1);
  }
  let start = Math.max(2, cur - siblings);
  let end = Math.min(last - 1, cur + siblings);
  // keep the slot count constant near the edges (1 2 3 4 5 … N / 1 … N-4 … N)
  const span = 2 * siblings + 2;
  if (cur - siblings <= 3) {
    start = 2;
    end = Math.max(end, 1 + span);
  }
  if (cur + siblings >= last - 2) {
    end = last - 1;
    start = Math.min(start, last - span);
  }
  const items: PagerItem[] = [1];
  if (start > 2) items.push('gap-left');
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end < last - 1) items.push('gap-right');
  items.push(last);
  return items;
}

// ---------------------------------------------------------------------------
// status pill (History row, prototype statusPill)
// ---------------------------------------------------------------------------

/** Tailwind classes + label for a History status pill (prototype `statusPill`). */
export interface StatusPill {
  readonly className: string;
  readonly label: string;
}

const STATUS_PILL: Record<ResearchRequestStatus, StatusPill> = {
  published: { className: 'bg-green-100 text-green-700', label: 'Published' },
  in_review: { className: 'bg-amber-100 text-amber-700', label: 'In review' },
  failed: { className: 'bg-red-100 text-red-700', label: 'Failed' },
  draft: { className: 'bg-slate-100 text-slate-600', label: 'Draft' },
  running: { className: 'bg-blue-100 text-blue-700', label: 'Running' },
  queued: { className: 'bg-blue-100 text-blue-700', label: 'Queued' },
  cancelled: { className: 'bg-slate-100 text-slate-500', label: 'Cancelled' },
};

export function statusPill(status: ResearchRequestStatus): StatusPill {
  return STATUS_PILL[status] ?? { className: 'bg-slate-100 text-slate-600', label: status };
}

// ---------------------------------------------------------------------------
// open-by-status routing (T04)
// ---------------------------------------------------------------------------

/**
 * Where a History row opens, given its status (T04). Prefers the server-computed
 * `opens_to` already on the row; falls back to deriving it from the status so the
 * helper also works on a freshly cloned draft. Returns the screen id.
 */
export function openTarget(row: Pick<HistoryRequestRow, 'status' | 'opens_to'>): ResearchScreen {
  return row.opens_to ?? opensToScreen(row.status);
}

/** "Open → Run" button caption for a History row (T04 acceptance #4). */
export function openButtonCaption(row: Pick<HistoryRequestRow, 'status' | 'opens_to'>): string {
  return `Open → ${RESEARCH_SCREEN_LABELS[openTarget(row)]}`;
}

// ---------------------------------------------------------------------------
// date formatting (display only)
// ---------------------------------------------------------------------------

/** Format an ISO date to `YYYY-MM-DD` for the History date column; '' on bad input. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** Verified-date suffix ("verified <date>") for a Library Source cell, or ''. */
export function verifiedLabel(isoOrNull: string | null): string {
  const d = formatDate(isoOrNull);
  return d ? `verified ${d}` : '';
}

// ---------------------------------------------------------------------------
// industry filter options (T02 acceptance #2)
// ---------------------------------------------------------------------------

/**
 * Options for the Library industry dropdown (after the fixed "All industries").
 * An explicit `industries` prop wins (host override / tests); otherwise the live
 * research dictionary (`GET /research/dictionaries` → `industry`) is used. Before
 * this, no host passed the prop, so the dropdown only ever showed "All".
 * Blank values are dropped and duplicates collapsed, order preserved.
 */
export function industryFilterOptions(
  explicit: readonly string[] | undefined,
  dictionary: readonly string[] | undefined,
): readonly string[] {
  const source = explicit && explicit.length > 0 ? explicit : (dictionary ?? []);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of source) {
    const v = raw.trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}
